import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseService } from './base.service';
import { GlobalService } from './global.service';
import { environment } from 'src/environments/environment';

declare const Stripe: any;

export interface StripeSetupIntentInfo {
  businessId: number;
  stripeCustomerId: string;
  clientSecret: string;
  publishableKey: string;
  freeTrialEndDate?: string;
  amountDueAtTrialEnd?: number;
  chargeDate?: string;
}

export interface StripeSetupIntentResult {
  success: boolean;
  message?: string;
  data?: StripeSetupIntentInfo;
}

export interface AttachedCardInfo {
  cardOnFile: boolean;
  cardBrand?: string;
  cardLast4?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
}

export interface AttachPaymentMethodResult {
  success: boolean;
  message?: string;
  data?: AttachedCardInfo;
}

export interface StripePaymentMethodSummary {
  paymentMethodId: string;
  cardBrand: string;
  cardLast4: string;
  cardExpMonth: number;
  cardExpYear: number;
  isDefault: boolean;
  addedDate?: string;
}

export interface ListPaymentMethodsResult {
  success: boolean;
  message?: string;
  data: StripePaymentMethodSummary[];
}

/** Emitted by StripeAddCardComponent once attachPaymentMethod() confirms the card is on file —
 *  the canonical shape every consumer (checklist, billing-address, trial-lockout overlay) reads
 *  to patch their own local copy of GlobalService.userInfo. */
export interface StripeCardSavedEvent {
  paymentMethodId: string;
  cardBrand?: string;
  cardLast4?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
}

export interface SimpleCardActionResult {
  success: boolean;
  message?: string;
}

/** Wraps the auth-svc billing-card flow:
 *  - `v3/subscription/setup-intent` (POST `{UserID, BusinessId}`) creates/reuses a Stripe
 *    SetupIntent for the merchant's billing customer and returns its clientSecret + the
 *    publishableKey to use.
 *  - The card is attached to Stripe by confirming that SetupIntent client-side
 *    (StripeAddCardComponent.saveCard() → stripe.confirmCardSetup()).
 *  - `v3/subscription/payment-method` (attachPaymentMethod()) tells the backend which
 *    PaymentMethod to keep — it's appended to the business's saved cards as the new default and
 *    its brand/last4/expiry persisted onto BusinessSetup.Subscription.TransactionDetails (what
 *    BusinessSetupProgressService.hasCardOnFile reads for the checklist's "Add card" step — real
 *    backend state, not a localStorage flag, so it survives a fresh login on any device).
 *  - `v3/subscription/payment-methods` (GET, listPaymentMethods()) lists every card on file for
 *    the business, `.../payment-method/default` (setDefaultPaymentMethod()) and
 *    `.../payment-method/remove` (removePaymentMethod()) manage them — a business can have more
 *    than one card; only one is ever the default. */
@Injectable({ providedIn: 'root' })
export class StripeCardService {
  private readonly stripeInstances: { [publishableKey: string]: any } = {};

  constructor(private readonly _bs: BaseService) {}

  /** UserID/BusinessId are what every endpoint below identifies the caller by. */
  private currentIdentity(): { userId: any; businessId: number } {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    return {
      userId: setup?.userID ?? setup?.UserID ?? GlobalService.userInfo?.userID ?? GlobalService.userInfo?.UserID,
      businessId: Number(setup?.businessId ?? setup?.BusinessId)
    };
  }

  /** This family of endpoints responds in PascalCase (Success/Message/Data) rather than the
   *  success/data casing BaseService.processErrors() normally expects, and some responses aren't
   *  wrapped in a Data envelope at all — read both casings defensively (same convention already
   *  documented for GetSubscriptions field casing) and fall back to the raw body when unwrapped. */
  private unwrap(res: any): { success: boolean; message?: string; rawData: any } {
    const success = res?.Success ?? res?.success;
    const rawData = res?.Data ?? res?.data ?? res;
    return { success: !!success, message: res?.Message ?? res?.message, rawData };
  }

  createSetupIntent(): Observable<StripeSetupIntentResult> {
    const { userId, businessId } = this.currentIdentity();
    const url = environment.AuthenticationURL + 'v3/subscription/setup-intent';
    return this._bs.httpPost(url, { UserID: userId, BusinessId: businessId }).pipe(
      map((res: any) => {
        const { success, message, rawData } = this.unwrap(res);
        const clientSecret = rawData?.ClientSecret ?? rawData?.clientSecret;
        if (!success || !clientSecret) {
          return { success: false, message: message || 'Unable to start card setup. Please try again.' };
        }
        return {
          success: true,
          data: {
            businessId: rawData.BusinessId ?? rawData.businessId,
            stripeCustomerId: rawData.StripeCustomerId ?? rawData.stripeCustomerId,
            clientSecret,
            publishableKey: rawData.PublishableKey ?? rawData.publishableKey ?? environment.StripePublishableKey,
            freeTrialEndDate: rawData.FreeTrialEndDate ?? rawData.freeTrialEndDate,
            amountDueAtTrialEnd: rawData.AmountDueAtTrialEnd ?? rawData.amountDueAtTrialEnd,
            chargeDate: rawData.ChargeDate ?? rawData.chargeDate
          }
        };
      })
    );
  }

  attachPaymentMethod(paymentMethodId: string): Observable<AttachPaymentMethodResult> {
    const { userId, businessId } = this.currentIdentity();
    const url = environment.AuthenticationURL + 'v3/subscription/payment-method';
    return this._bs.httpPost(url, { UserID: userId, BusinessId: businessId, PaymentMethodId: paymentMethodId }).pipe(
      map((res: any) => {
        const { success, message, rawData } = this.unwrap(res);
        const cardLast4 = rawData?.CardLast4 ?? rawData?.cardLast4;
        const cardOnFile = rawData?.CardOnFile ?? rawData?.cardOnFile ?? !!cardLast4;

        if (!success || !cardOnFile) {
          return { success: false, message: message || 'Failed to save card. Please try again.' };
        }
        return {
          success: true,
          data: {
            cardOnFile: true,
            cardBrand: rawData?.CardBrand ?? rawData?.cardBrand,
            cardLast4,
            cardExpMonth: rawData?.CardExpMonth ?? rawData?.cardExpMonth,
            cardExpYear: rawData?.CardExpYear ?? rawData?.cardExpYear
          }
        };
      })
    );
  }

  listPaymentMethods(): Observable<ListPaymentMethodsResult> {
    const { userId, businessId } = this.currentIdentity();
    const url = environment.AuthenticationURL + 'v3/subscription/payment-methods';
    return this._bs.httpGetParams(url, { UserID: userId, BusinessId: businessId }).pipe(
      map((res: any) => {
        const { success, message, rawData } = this.unwrap(res);
        if (!success || !Array.isArray(rawData)) {
          return { success: false, message: message || 'Unable to load saved cards.', data: [] };
        }
        return {
          success: true,
          data: rawData.map((item: any) => ({
            paymentMethodId: item.PaymentMethodId ?? item.paymentMethodId,
            cardBrand: item.CardBrand ?? item.cardBrand,
            cardLast4: item.CardLast4 ?? item.cardLast4,
            cardExpMonth: item.CardExpMonth ?? item.cardExpMonth,
            cardExpYear: item.CardExpYear ?? item.cardExpYear,
            isDefault: !!(item.IsDefault ?? item.isDefault),
            addedDate: item.AddedDate ?? item.addedDate
          }))
        };
      })
    );
  }

  setDefaultPaymentMethod(paymentMethodId: string): Observable<SimpleCardActionResult> {
    const { userId, businessId } = this.currentIdentity();
    const url = environment.AuthenticationURL + 'v3/subscription/payment-method/default';
    return this._bs.httpPost(url, { UserID: userId, BusinessId: businessId, PaymentMethodId: paymentMethodId }).pipe(
      map((res: any) => {
        const { success, message } = this.unwrap(res);
        return success ? { success: true } : { success: false, message: message || 'Failed to set default card.' };
      })
    );
  }

  removePaymentMethod(paymentMethodId: string): Observable<SimpleCardActionResult> {
    const { userId, businessId } = this.currentIdentity();
    const url = environment.AuthenticationURL + 'v3/subscription/payment-method/remove';
    return this._bs.httpPost(url, { UserID: userId, BusinessId: businessId, PaymentMethodId: paymentMethodId }).pipe(
      map((res: any) => {
        const { success, message } = this.unwrap(res);
        return success ? { success: true } : { success: false, message: message || 'Failed to remove card.' };
      })
    );
  }

  /** Stripe instances are keyed by publishable key (returned per-request by the backend above,
   *  not a static environment value) so a stale/rotated key never reuses a cached client. */
  getStripe(publishableKey: string): any {
    if (typeof Stripe === 'undefined') {
      throw new Error('Stripe.js has not loaded yet — check the script tag in index.html.');
    }
    if (!this.stripeInstances[publishableKey]) {
      this.stripeInstances[publishableKey] = Stripe(publishableKey);
    }
    return this.stripeInstances[publishableKey];
  }
}
